import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

export function VariantsPage() {
  const qc = useQueryClient();
  const [productId, setProductId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const prodQuery = useQuery({
    queryKey: ['products-variant-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });
  const selectedProduct = useQuery({
    queryKey: ['product', productId],
    queryFn: () => inventoryApi.getProduct(productId),
    enabled: !!productId,
  });
  const variantsQuery = useQuery({
    queryKey: ['inv', 'variants', productId],
    queryFn: () => inventoryApi.listVariants(productId),
    enabled: !!productId,
  });

  const deleteMut = useMutation({
    mutationFn: ({ pid, vid }: any) => inventoryApi.deleteVariant(pid, vid),
    onSuccess: () => { toast.success('Variant deleted'); qc.invalidateQueries({ queryKey: ['inv', 'variants', productId] }); setDeletingId(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Inventory" title="Product variants"
        subtitle="Size, color, flavor, and other variations of a base product."
        actions={productId ? <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New variant</button> : null}
      />

      {/* Product picker */}
      <div className="card p-5 mb-6">
        <div className="eyebrow text-ink-500 mb-2">Product</div>
        {selectedProduct.data ? (
          <div className="flex gap-3 items-center bg-canvas-subtle rounded-xl px-3 py-2.5">
            <span className="font-mono text-xs font-semibold text-ink-900">{selectedProduct.data.sku}</span>
            <span className="text-sm text-ink-700 flex-1">{selectedProduct.data.name}</span>
            <button className="text-xs text-ink-500 hover:text-ink-900 transition-colors" onClick={() => { setProductId(''); setProductSearch(''); }}>Change ×</button>
          </div>
        ) : (
          <div className="relative">
            <input type="text" placeholder="Type SKU or product name to search…" value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="input" />
            {(prodQuery.data?.data?.length ?? 0) > 0 && productSearch.length >= 2 && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 card shadow-lg overflow-hidden">
                {prodQuery.data!.data.map((p: any) => (
                  <button key={p.id} className="w-full text-left px-4 py-2.5 text-sm hover:bg-canvas-subtle transition-colors border-b border-ink-100 last:border-b-0"
                    onClick={() => { setProductId(p.id); setProductSearch(''); }}>
                    <span className="font-mono text-xs font-semibold text-ink-700 mr-2">{p.sku}</span>
                    <span className="text-ink-900">{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!productId ? (
        <div className="card"><EmptyState message="Select a product" hint="Search for a product above to manage its variants." /></div>
      ) : variantsQuery.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : variantsQuery.error ? (
        <ErrorState error={variantsQuery.error} />
      ) : !variantsQuery.data?.length ? (
        <div className="card">
          <EmptyState message="No variants yet" hint="Add size, color, or other attribute variations to this product."
            action={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New variant</button>}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Variant SKU</th><th>Barcode</th><th>Attributes</th>
                  <th className="text-right">Price adj.</th><th>Status</th>
                  <th className="text-right w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(variantsQuery.data ?? []).map((v: any) => {
                  const adj = parseFloat(v.price_adjustment);
                  return (
                    <tr key={v.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{v.sku}</td>
                      <td className="font-mono text-xs text-ink-600">{v.barcode ?? <span className="text-ink-400">—</span>}</td>
                      <td>
                        {v.attributes ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(v.attributes).map(([k, val]: any) => (
                              <span key={k} className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 text-2xs">
                                <span className="text-ink-500">{k}:</span>&nbsp;{val}
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-ink-400 text-xs">—</span>}
                      </td>
                      <td className={`text-right font-mono text-sm tabular-nums ${adj > 0 ? 'text-sage-700' : adj < 0 ? 'text-danger' : 'text-ink-500'}`}>
                        {adj > 0 ? '+' : ''}${adj.toFixed(2)}
                      </td>
                      <td><StatusBadge status={v.status} /></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                            onClick={() => setEditing(v)}>Edit</button>
                          <button className="text-xs font-medium px-2 py-1 rounded text-danger hover:bg-red-50 transition-colors"
                            onClick={() => setDeletingId(v.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && productId && (
        <CreateVariantModal productId={productId} baseSku={selectedProduct.data?.sku ?? ''}
          onClose={() => setShowCreate(false)}
          onCreated={() => { qc.invalidateQueries({ queryKey: ['inv', 'variants', productId] }); setShowCreate(false); }} />
      )}
      {editing && productId && (
        <EditVariantModal variant={editing} productId={productId}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['inv', 'variants', productId] }); setEditing(null); }} />
      )}
      {deletingId && (
        <Modal
          onClose={() => setDeletingId(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Inventory · Variants</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete variant?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">This variant will be permanently removed.</p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingId(null)}>Cancel</button>
            <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate({ pid: productId, vid: deletingId })}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete variant'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CreateVariantModal({ productId, baseSku, onClose, onCreated }: {
  productId: string; baseSku: string; onClose: () => void; onCreated: () => void;
}) {
  const [sku, setSku] = useState('');
  const [barcode, setBarcode] = useState('');
  const [priceAdjustment, setPriceAdjustment] = useState('0');
  const [attributes, setAttributes] = useState([{ key: '', value: '' }]);

  const mut = useMutation({
    mutationFn: () => {
      const attrObj: Record<string, string> = {};
      for (const { key, value } of attributes) { if (key && value) attrObj[key.toLowerCase()] = value; }
      return inventoryApi.createVariant(productId, {
        sku: sku.trim(), barcode: barcode.trim() || null,
        attributes: Object.keys(attrObj).length > 0 ? attrObj : undefined,
        priceAdjustment: parseFloat(priceAdjustment) || 0,
      });
    },
    onSuccess: () => { toast.success('Variant created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New variant</h2>
        <p className="text-sm text-ink-500 mt-1">Add a size, color, or other variation to this product.</p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">SKU <span className="text-danger">*</span></label>
          <input className="input font-mono" value={sku} onChange={(e) => setSku(e.target.value)} placeholder={baseSku ? `${baseSku}-RED-M` : 'variant SKU'} />
        </div>
        <div>
          <label className="label">Barcode <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        </div>
        <div>
          <label className="label">Price adjustment</label>
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">$</span>
            <input type="number" step="0.01" className="input pl-6 w-32 tabular-nums" value={priceAdjustment} onChange={(e) => setPriceAdjustment(e.target.value)} /></div>
          <div className="text-2xs text-ink-500 mt-1.5">Added to parent's base price (can be negative).</div>
        </div>
        <div>
          <label className="label">Attributes</label>
          <div className="space-y-2">
            {attributes.map((attr, i) => (
              <div key={i} className="flex gap-2">
                <input className="input flex-1 text-xs" placeholder="color" value={attr.key} onChange={(e) => setAttributes((prev) => prev.map((a, j) => j === i ? { ...a, key: e.target.value } : a))} />
                <input className="input flex-1 text-xs" placeholder="red" value={attr.value} onChange={(e) => setAttributes((prev) => prev.map((a, j) => j === i ? { ...a, value: e.target.value } : a))} />
                {attributes.length > 1 && <button className="text-ink-400 hover:text-danger text-sm px-1" onClick={() => setAttributes((prev) => prev.filter((_, j) => j !== i))}>✕</button>}
              </div>
            ))}
            <button className="text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
              onClick={() => setAttributes((prev) => [...prev, { key: '', value: '' }])}>+ Add attribute</button>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!sku.trim() || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create variant'}
        </button>
      </div>
    </Modal>
  );
}

function EditVariantModal({ variant, productId, onClose, onSaved }: {
  variant: any; productId: string; onClose: () => void; onSaved: () => void;
}) {
  const [barcode, setBarcode] = useState(variant.barcode ?? '');
  const [priceAdjustment, setPriceAdjustment] = useState(String(parseFloat(variant.price_adjustment) || 0));
  const [status, setStatus] = useState(variant.status ?? 'active');

  const mut = useMutation({
    mutationFn: () => inventoryApi.updateVariant(productId, variant.id, {
      barcode: barcode.trim() || null,
      priceAdjustment: parseFloat(priceAdjustment) || 0,
      status,
    }),
    onSuccess: () => { toast.success('Variant updated'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Edit variant</h2>
        <p className="text-sm text-ink-500 mt-1 font-mono">{variant.sku}</p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Barcode <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        </div>
        <div>
          <label className="label">Price adjustment</label>
          <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">$</span>
            <input type="number" step="0.01" className="input pl-6 w-32 tabular-nums" value={priceAdjustment} onChange={(e) => setPriceAdjustment(e.target.value)} /></div>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
